import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  devicePlusModalRecoil,
  LoadingRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import LabelType1 from '@/component/LabelType1';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Chart, registerables } from 'chart.js';
import { FacilityApi } from '@/sdk/FacilityApi';
import {
  FacilityControllerGetFacilityDevicesParams,
  DeviceDtoRx,
  CreateFacilityReportDtoRx,
  FacilityControllerCreateFacilityReportParams,
} from '@/sdk/data-contracts';
import { facilitiesInstallationDeviceListRecoil } from '@/atom/FacilitiesAtom';
import DevicePlusModal from '@/modal/DevicePlusModal';
import Loading from '@/component/Loading';

const InstallationDevice = () => {
  const { id } = useParams<{ id: string }>();
  const facilityApi = new FacilityApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [loadingState, setLoadingState] = useRecoilState(LoadingRecoil);
  const [devicePlusModalState, setDevicePlusModalState] = useRecoilState(
    devicePlusModalRecoil,
  );
  const [
    facilitiesInstallationDeviceListState,
    setFacilitiesInstallationDeviceListState,
  ] = useRecoilState(facilitiesInstallationDeviceListRecoil);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [listLength, setListLength] = useState<number>(0);

  const downloadCSV = async (saveFileName: string) => {
    let allList: DeviceDtoRx[] | undefined;

    const dto: FacilityControllerGetFacilityDevicesParams = {
      id: Number(id),
      sortBy,
      order,
    };
    try {
      const { data } = await facilityApi.facilityControllerGetFacilityDevices(
        dto,
        {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (data.devices) allList = data.devices;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'No',
      '디바이스 No	',
      'EUI',
      '디바이스명	',
      '설치위치',
      '통신상태',
      '센서상태',
      '설치일자',
    ];

    const rows = allList.map((list, index) => [
      index + 1,
      list.number,
      list.devEui,
      list.name,
      list.location,
      list.communicationStatus,
      list.sensorStatus,
      list.setUpDate,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filterInstallationDeviceList = async () => {
    const dto: FacilityControllerGetFacilityDevicesParams = {
      id: Number(id),
      searchKeyword,
      offset,
      limit,
      sortBy,
      order,
    };
    try {
      const { data } = await facilityApi.facilityControllerGetFacilityDevices(
        dto,
        {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (data.devices) setFacilitiesInstallationDeviceListState(data.devices);
      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setOffset(0);
    filterInstallationDeviceList();
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
  };

  Chart.register(...registerables);

  const openAndSavePDF = async () => {
    let pdfDatas: CreateFacilityReportDtoRx | null = null;

    if (startDate === '' || endDate === '') {
      setErrorModalText('조회할 기간을 선택해 주세요.');
      return;
    }

    const dto: FacilityControllerCreateFacilityReportParams = {
      startDate: startDate,
      endDate: endDate,
      id: Number(id),
    };

    try {
      const { data } = await facilityApi.facilityControllerCreateFacilityReport(
        dto,
        {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        },
      );
      pdfDatas = data;
    } catch (err: any) {}

    if (pdfDatas?.datas.length === 0) {
      setErrorModalText('해당 날짜에 데이터 값이 없습니다.');
      return;
    }

    setLoadingState(true);

    const deviceInfoList = pdfDatas?.deviceInformation
      .map((device) => {
        return `${device.modelName} : ${device.deviceCount}대`;
      })
      .join(' / ');

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (pdfDatas !== null) {
      for (let i = 0; i < pdfDatas.datas.length; i++) {
        const dataDetails = pdfDatas.datas[i].dataDetails;

        for (let j = 0; j < dataDetails.length; j++) {
          const dates = dataDetails[j].dates;

          if (dates.length > 1) {
            dataDetails[j].dates = dates.map((date, index) => {
              if (index === 0 || index === dates.length - 1) {
                return formatDate(date);
              }
              return '';
            });
          }
        }
      }
    }

    const pdf = new jsPDF('p', 'mm', 'a4');

    const pdfContent = document.createElement('div');
    pdfContent.style.width = '210mm';
    pdfContent.style.height = '297mm';
    pdfContent.innerHTML = `
      <div class="facilities-first-pdf-box">
        <h1>IoT 센서 월간 Report</h1>
        <h2>1. ${pdfDatas?.facilityName} (${pdfDatas?.facilityAddress})</h2>
        <ul>
          <li>- 총 운영 디바이스 : ${pdfDatas?.totalDeviceCount}</li>
          <li>- 디바이스 정보 : ${deviceInfoList}</li>
          <li>- 기간 : ${startDate} ~ ${endDate}</li>
        </ul>
      </div>
    `;

    document.body.appendChild(pdfContent);
    const canvas = await html2canvas(pdfContent);
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = 190;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const position = 10;
    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
    document.body.removeChild(pdfContent);

    if (pdfDatas?.datas) {
      for (let i = 0; i < pdfDatas.datas.length; i++) {
        pdf.addPage();

        const dataContent = document.createElement('div');
        dataContent.style.width = '210mm';
        dataContent.style.height = '297mm';

        dataContent.innerHTML = `
          <div class='facilities-pdf-box'>
            <h1>${pdfDatas.datas[i].deviceName}</h1>
            <table>
              <thead>
                <tr>
                  <th>구분</th>
                  <th>최저</th>
                  <th>최고</th>
                  <th>변화량</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  ${pdfDatas.datas[i].dataDetails
                    .map(
                      (detail) => `
                    <tr>
                      <td>${detail.dataName}</td>
                      <td>${detail.minData}</td>
                      <td>${detail.maxData}</td>
                      <td>${detail.changeData}</td>
                    </tr>
                  `,
                    )
                    .join('')}
                </tr>
              </tbody>
            </table>
          </div>
        `;

        const chartsContainer = document.createElement('div');
        chartsContainer.className = 'charts-container';
        chartsContainer.style.display = 'flex';
        chartsContainer.style.flexWrap = 'wrap';
        chartsContainer.style.gap = '10px';
        dataContent.appendChild(chartsContainer);

        if (pdfDatas.datas[i].dataDetails) {
          pdfDatas.datas[i].dataDetails.forEach((detail, j) => {
            const chartCanvas = document.createElement('canvas');
            chartsContainer.appendChild(chartCanvas);

            new Chart(chartCanvas, {
              type: 'line',
              data: {
                labels: detail.dates,
                datasets: [
                  {
                    label: detail.dataName,
                    data: detail.datas,
                    borderColor: 'rgba(75, 192, 192, 1)',
                    backgroundColor: 'rgba(75, 192, 192, 1)',
                  },
                ],
              },
              options: {
                responsive: false,
                scales: {
                  x: {
                    beginAtZero: true,
                    ticks: {
                      font: {
                        size: 10,
                      },
                      autoSkip: false,
                      maxRotation: 0,
                      minRotation: 0,
                    },
                  },
                  y: { beginAtZero: true },
                },
                plugins: {
                  legend: {
                    position: 'bottom',
                  },
                },
              },
            });
          });
        }

        document.body.appendChild(dataContent);

        await new Promise((resolve) => setTimeout(resolve, 500));

        const pageCanvas = await html2canvas(dataContent);
        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageImgHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;
        pdf.addImage(pageImgData, 'PNG', 10, 10, imgWidth, pageImgHeight);

        document.body.removeChild(dataContent);
      }
    }

    setLoadingState(false);
    pdf.save('facility.pdf');
  };

  useEffect(() => {
    filterInstallationDeviceList();
  }, [limit, offset]);

  return (
    <>
      <TabCntBox>
        <InputArea>
          <IS.TextInputType1
            style={{ width: 145 }}
            type="date"
            value={startDate}
            onChange={(e) => {
              if (endDate !== '') {
                if (e.target.value > endDate) {
                  setErrorModalText('시작일은 종료일보다 클 수 없습니다.');
                  return;
                }
              }
              setStartDate(e.target.value);
            }}
          />
          <h2>~</h2>
          <IS.TextInputType1
            style={{ width: 145 }}
            type="date"
            value={endDate}
            onChange={(e) => {
              if (startDate !== '') {
                if (e.target.value < startDate) {
                  setErrorModalText('종료일은 시작일보다 클 수 없습니다.');
                  return;
                }
              }
              setEndDate(e.target.value);
            }}
          />
          <BS.Btn1Type1 type="button" onClick={openAndSavePDF}>
            리포트 생성
          </BS.Btn1Type1>
        </InputArea>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <form onSubmit={handleSearch}>
              <InputArea>
                <IS.TextInputType1
                  style={{ width: 238 }}
                  type="text"
                  placeholder="디바이스No, EUI, 이름 입력"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
                <SearchBtn />
              </InputArea>
            </form>
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5
              type="button"
              onClick={() => downloadCSV('installation_device')}
            >
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
            <BS.Btn1Type1
              type="button"
              onClick={() => setDevicePlusModalState(true)}
            >
              디바이스 추가
            </BS.Btn1Type1>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>No</th>
                <th>디바이스 No</th>
                <th>EUI</th>
                <th>디바이스명</th>
                <th>설치위치</th>
                <th>통신상태</th>
                <th>센서상태</th>
                <th>설치일자</th>
              </tr>
            </thead>
            <tbody>
              {facilitiesInstallationDeviceListState?.map((list, index) => (
                <tr key={index}>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td>{list.number}</td>
                  <td>{list.devEui}</td>
                  <td>{list.name}</td>
                  <td>{list.location}</td>
                  <td>
                    {list.communicationStatus !== '' && (
                      <LabelType1 labelValue={list.communicationStatus} />
                    )}
                  </td>
                  <td>
                    {list.sensorStatus !== '' && (
                      <LabelType1 labelValue={list.sensorStatus} />
                    )}
                  </td>
                  <td>{list.setUpDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={listLength}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </TabCntBox>
      {devicePlusModalState && <DevicePlusModal />}
      {loadingState && <Loading />}
    </>
  );
};

export default InstallationDevice;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const CntBox2 = styled(CS.CntBoxType1)`
  width: 100%;
  margin-top: 20px;
`;

export const InforWrap = styled.div`
  width: 100%;
  display: flex;
  gap: 20px;
  margin-top: 16px;
`;

export const InforBox1 = styled(CS.CntBoxType2)`
  position: relative;
  width: calc(50% - 13.3333px);
  > .device-btn {
    position: absolute;
    right: 20px;
    bottom: 20px;
  }
`;

export const InforBox2 = styled(CS.CntBoxType2)`
  width: calc(25% - 13.3333px);
  > .text-wrap {
    &:before {
      display: none;
    }
    > .text-box {
      width: 100%;
      &:first-child {
        padding-right: 0;
      }
      &:last-child {
        padding-left: 0;
      }
    }
  }
`;

export const InforBox3 = styled(CS.CntBoxType2)`
  width: calc(25% - 13.3333px);
  > .img-area {
    width: 100%;
    position: relative;
    margin-top: 12px;
    > button {
      position: absolute;
      width: 34px;
      height: 34px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      border: 1px solid #ececec;
      background: #fff;
      border-radius: 4px;
      left: 10px;
      bottom: 10px;
      > .img-box {
        width: 24px;
      }
    }
  }
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > h2 {
    color: #555;
    font-size: 14px;
  }
`;

export const TabCntBox = styled.div`
  width: 100%;
  margin-top: 20px;
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableOpitonBox2 = styled(TableOpitonBox)`
  margin-top: 32px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const MapBox = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
`;

export const MapModal = styled.div`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 60%;
  transform: translate(-50%, -50%);
  width: 136px;
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  box-shadow: 4px 4px 12px 0px rgba(255, 255, 255, 0.25);
  overflow: hidden;
  > .title-box {
    box-sizing: border-box;
    padding: 8px 12px;
    background: #e9e8e8;
    font-size: 16px;
    font-weight: 500;
  }
  > .text-box {
    box-sizing: border-box;
    padding: 12px;
    background: #fff;
    > .state-box {
      display: flex;
      align-items: center;
      gap: 8px;
      > .text {
        font-size: 15px;
      }
      > .line {
        width: 1px;
        height: 10px;
        background: #d9d9d9;
      }
    }
    > .date {
      margin-top: 6px;
      font-size: 12px;
      font-weight: 300;
      color: #777;
    }
    > button {
      margin-top: 8px;
      font-size: 14px;
      color: #3c8deb;
    }
  }
`;

export const MapTextModal = styled.div`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 70%;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  padding: 10px 16px;
  box-shadow: 4px 4px 12px 0px rgba(255, 255, 255, 0.25);
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  > p {
    font-size: 15px;
    white-space: nowrap;
  }
  > button {
    width: 10px;
    min-width: 10px;
  }
`;

export const GraphBox = styled.div`
  margin-top: 16px;
  width: 100%;
  height: 280px;
  box-sizing: border-box;
  border-top: 2px solid #222222;
`;
